import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Clock, MapPin, RefreshCw, Search, X } from 'lucide-react';
import api from '../../services/api';
import { usePermissions } from '../../contexts/PermissionContext';
import { formatDateTime } from '../../utils/formatDate';
import BookingTrackingPanel from './BookingTrackingPanel';
import CarrierLogo from './CarrierLogo';

// Tracking board: every live shipment in a numbered column for its status,
// showing who booked it, its AWB and where it has got to (the latest entry in
// its tracking history). A card opens the shipment's tracking; staff who can
// edit bookings add the next update there, or tick several cards and move them
// together. Data: GET /admin/tracking/board (routes/admin_tracking.py).

const STATUS = {
  booked: { label: 'Booked', dot: 'bg-slate-400', bar: 'border-t-slate-400' },
  pickup_scheduled: { label: 'Pickup scheduled', dot: 'bg-amber-400', bar: 'border-t-amber-400' },
  picked_up: { label: 'Picked up', dot: 'bg-sky-500', bar: 'border-t-sky-500' },
  in_transit: { label: 'In transit', dot: 'bg-indigo-500', bar: 'border-t-indigo-500' },
  out_for_delivery: { label: 'Out for delivery', dot: 'bg-violet-500', bar: 'border-t-violet-500' },
  delivered: { label: 'Delivered', dot: 'bg-emerald-500', bar: 'border-t-emerald-500' },
  failed: { label: 'Failed', dot: 'bg-red-500', bar: 'border-t-red-500' },
  returned: { label: 'Returned', dot: 'bg-orange-500', bar: 'border-t-orange-500' },
  lost: { label: 'Lost', dot: 'bg-rose-600', bar: 'border-t-rose-600' },
  cancelled: { label: 'Cancelled', dot: 'bg-gray-400', bar: 'border-t-gray-400' },
};
// What staff can move a shipment to (the server's list; "lost" is not one).
const UPDATE_TO = ['booked', 'pickup_scheduled', 'picked_up', 'in_transit', 'out_for_delivery', 'delivered', 'failed', 'returned', 'cancelled'];

const label = (s) => STATUS[s]?.label || titleCase(s);
function titleCase(s) {
  return s ? String(s).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '—';
}

// Stored times are UTC without a zone (datetime.utcnow().isoformat()).
const toMs = (iso) => {
  if (!iso) return null;
  const s = String(iso);
  const t = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : `${s}Z`).getTime();
  return Number.isNaN(t) ? null : t;
};
const ago = (iso) => {
  const t = toMs(iso);
  if (t == null) return '';
  const m = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.round(h / 24)}d`;
};
const money = (n) => {
  const v = Number(n);
  if (!v) return null;
  if (v < 1000) return `₹${Math.round(v)}`;
  if (v < 100000) return `₹${(v / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  return `₹${(v / 100000).toFixed(1).replace(/\.0$/, '')}L`;
};

const field = 'h-8 border border-slate-300 bg-white px-2 text-[13px] text-slate-800 focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-400';

// Our own hubs, and placeholders, say nothing about where the parcel is -
// the card shows who carries it instead.
const OWN_PLACE = /xfas|send\s*my\s*parcel|customer\s*request|admin\s*update/i;

// ------------------------------------------------------------------ card
const Card = ({ card, onOpen, selectable, selected, onToggle }) => {
  const when = card.latest?.timestamp || card.updated_at;
  const place = card.latest?.location && !OWN_PLACE.test(card.latest.location) ? card.latest.location : null;
  const open = () => onOpen(card);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
      data-test="track-card"
      data-id={card.id}
      className={`group cursor-pointer px-2.5 py-2 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-orange-400 ${
        selected ? 'bg-orange-50' : 'bg-white hover:bg-slate-50'}`}
    >
      <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
        {selectable ? (
          <input
            type="checkbox"
            checked={selected}
            onClick={(e) => e.stopPropagation()}
            onChange={() => onToggle(card.id)}
            className="h-3.5 w-3.5 flex-shrink-0 rounded-none border-slate-300 text-orange-600 focus:ring-orange-400"
            aria-label={`Select ${card.shipment_number}`}
            data-test="card-select"
          />
        ) : null}
        <span className="font-mono font-semibold text-slate-700">{card.awb || card.shipment_number}</span>
        {card.carrier && place ? <span className="truncate">{`• ${card.carrier}`}</span> : null}
      </div>
      <p className="mt-1 truncate text-[13px] font-semibold text-slate-900" data-test="card-booker">{card.booker || 'Unknown'}</p>
      {place ? (
        <div className="mt-0.5 flex items-start gap-1 text-[12px] leading-snug" data-test="card-reached">
          <MapPin className="mt-0.5 h-3 w-3 flex-shrink-0 text-orange-500" aria-hidden="true" />
          <span className="line-clamp-2 text-slate-700" title={card.latest.description || undefined}>{place}</span>
        </div>
      ) : (
        <div className="mt-1 flex items-center" data-test="card-carrier" title={card.latest?.location || 'No updates yet'}>
          {card.carrier ? <CarrierLogo carrier={card.carrier} size="sm" /> : <span className="text-[12px] text-slate-400">No updates yet</span>}
        </div>
      )}
      <div className="mt-1 flex items-center justify-between gap-2 text-[11px] tabular-nums text-slate-500">
        <span>{[money(card.amount), ago(when)].filter(Boolean).join(' • ')}</span>
        {card.stale ? (
          <span className="inline-flex items-center gap-0.5 font-semibold text-amber-600" title="No update for 3+ days" data-test="card-delay">
            <span className="h-2 w-2 rounded-full bg-amber-400" aria-hidden="true" /> Delay
          </span>
        ) : (
          <span className="opacity-0 group-hover:opacity-100 text-slate-400" title={formatDateTime(when)}>
            <Clock className="inline h-3 w-3" aria-hidden="true" />
          </span>
        )}
      </div>
    </div>
  );
};

// ------------------------------------------------------------------ column
const Column = ({ n, col, onOpen, selectable, selected, onToggle, onToggleAll }) => {
  const s = STATUS[col.status] || STATUS.booked;
  const ids = col.cards.map((c) => c.id);
  const all = ids.length > 0 && ids.every((id) => selected.has(id));
  return (
    <section
      className={`flex min-h-0 flex-col border-t-2 ${s.bar} bg-white`}
      data-test={`col-${col.status}`}
      aria-label={label(col.status)}
    >
      <header className="flex items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-2.5 py-2">
        <span className="flex min-w-0 items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-700">
          {selectable && ids.length ? (
            <input
              type="checkbox"
              checked={all}
              onChange={() => onToggleAll(ids, !all)}
              className="h-3.5 w-3.5 rounded-none border-slate-300 text-orange-600 focus:ring-orange-400"
              aria-label={`Select every ${label(col.status)} card`}
            />
          ) : null}
          <span className={`h-2 w-2 flex-shrink-0 rounded-full ${s.dot}`} aria-hidden="true" />
          <span className="truncate">{`${n}. ${label(col.status)}`}</span>
        </span>
        <span className="bg-white px-1.5 text-[11px] font-semibold tabular-nums text-slate-600 ring-1 ring-slate-200" data-test="col-count">
          {col.count}
        </span>
      </header>
      <div className="min-h-0 flex-1 divide-y divide-slate-200 overflow-y-auto">
        {col.cards.length ? col.cards.map((c) => (
          <Card key={c.id} card={c} onOpen={onOpen} selectable={selectable} selected={selected.has(c.id)} onToggle={onToggle} />
        )) : (
          <p className="px-1 py-6 text-center text-[12px] text-slate-400">Nothing here</p>
        )}
        {col.count > col.cards.length ? (
          <p className="px-1 pt-1 text-center text-[11px] text-slate-400" data-test="col-more">{`(+${col.count - col.cards.length} more — search to narrow)`}</p>
        ) : null}
      </div>
    </section>
  );
};

// ------------------------------------------------------------------ updates
const UpdateFields = ({ status, setStatus, location, setLocation, note, setNote, prefix }) => (
  <>
    <select value={status} onChange={(e) => setStatus(e.target.value)} className={field} aria-label="Status" data-test={`${prefix}-status`}>
      {UPDATE_TO.map((s) => <option key={s} value={s}>{label(s)}</option>)}
    </select>
    <input value={location} onChange={(e) => setLocation(e.target.value)} className={field} placeholder="Location, e.g. London, UK" aria-label="Location" data-test={`${prefix}-location`} />
    <input value={note} onChange={(e) => setNote(e.target.value)} className={field} placeholder="Note the customer sees (optional)" aria-label="Note" data-test={`${prefix}-note`} />
  </>
);

const putStatus = (id, status, location, note) => api.put(`/admin/bookings/${id}/status`, {
  new_status: status,
  location: location.trim() || undefined,
  note: note.trim() || undefined,
});

const AddUpdate = ({ shipment, onSaved }) => {
  const [status, setStatus] = useState(shipment.status);
  const [location, setLocation] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await putStatus(shipment.id, status, location, note);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not save the update.');
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-2" data-test="add-update">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Add an update</p>
      <div className="grid grid-cols-2 gap-2 [&>*:nth-child(3)]:col-span-2">
        <UpdateFields prefix="update" {...{ status, setStatus, location, setLocation, note, setNote }} />
      </div>
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] text-slate-400">Shown on the customer&rsquo;s tracking page.</p>
        <button type="submit" disabled={saving} className="h-8 bg-slate-900 px-3 text-[13px] font-semibold text-white hover:bg-slate-700 disabled:opacity-60" data-test="update-save">
          {saving ? 'Saving…' : 'Save update'}
        </button>
      </div>
      {error ? <p className="text-[12px] text-red-600" role="alert">{error}</p> : null}
    </form>
  );
};

const BulkBar = ({ ids, onClear, onDone }) => {
  const [status, setStatus] = useState('in_transit');
  const [location, setLocation] = useState('');
  const [note, setNote] = useState('');
  const [progress, setProgress] = useState(null);

  const apply = async () => {
    let ok = 0;
    const failed = [];
    for (let i = 0; i < ids.length; i += 1) {
      setProgress(`Updating ${i + 1} of ${ids.length}…`);
      try {
        // One at a time: each writes its own tracking event and timestamp.
        // eslint-disable-next-line no-await-in-loop
        await putStatus(ids[i], status, location, note);
        ok += 1;
      } catch (e) {
        failed.push(ids[i]);
      }
    }
    setProgress(null);
    onDone(failed.length ? `${ok} moved to ${label(status)}; ${failed.length} could not be updated.` : `${ok} moved to ${label(status)}.`, failed);
  };

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-orange-200 bg-orange-50 px-3 py-2" data-test="bulk-bar">
      <span className="text-[13px] font-semibold text-slate-800">{`${ids.length} selected`}</span>
      <span className="text-[12px] text-slate-500">Move to</span>
      <UpdateFields prefix="bulk" {...{ status, setStatus, location, setLocation, note, setNote }} />
      <button type="button" onClick={apply} disabled={!!progress} className="h-8 bg-slate-900 px-3 text-[13px] font-semibold text-white hover:bg-slate-700 disabled:opacity-60" data-test="bulk-apply">
        {progress || 'Apply'}
      </button>
      <button type="button" onClick={onClear} disabled={!!progress} className="h-8 px-2 text-[13px] text-slate-600 hover:bg-orange-100">Clear</button>
    </div>
  );
};

// ------------------------------------------------------------------ pop-up
const TrackingPopup = ({ target, onClose, onChanged, canUpdate }) => {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    if (!target.id) {
      // An AWB that is not one of our shipments: the carrier's scans only.
      setDetail({ status: null, tracking_events: [], carrier_info: { tracking_number: target.awb } });
      return;
    }
    setError(null);
    api.get(`/admin/tracking/board/${target.id}`)
      .then((res) => setDetail(res.data.data))
      .catch(() => setError('Could not load this shipment.'));
  }, [target]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const d = detail;
  const s = d?.status ? STATUS[d.status] : null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="my-10 w-full max-w-xl bg-white shadow-2xl" role="dialog" aria-modal="true" aria-label="Shipment tracking" data-test="tracking-popup">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-3.5">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="truncate text-[15px] font-semibold text-slate-900">
                {target.id ? (d?.shipment_number || target.shipment_number) : `AWB ${target.awb}`}
              </h2>
              {s ? (
                <span className="inline-flex items-center gap-1 bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700">
                  <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} aria-hidden="true" />
                  {label(d.status)}
                </span>
              ) : null}
            </div>
            {target.id ? (
              <p className="mt-0.5 truncate text-[12px] text-slate-500">
                {[d?.booker || target.booker, d?.awb && `AWB ${d.awb}`, d?.carrier, [d?.from_city, d?.to_city].filter(Boolean).join(' → ')].filter(Boolean).join(' · ')}
              </p>
            ) : (
              <p className="mt-0.5 text-[12px] text-slate-500">Not one of our shipments &mdash; showing the carrier&rsquo;s scans only.</p>
            )}
          </div>
          <button type="button" onClick={onClose} className="p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Close" data-test="popup-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto px-5 py-4">
          {error ? <p className="text-sm text-red-600">{error}</p> : !d ? (
            <p className="text-sm text-slate-400">Loading&hellip;</p>
          ) : (
            <BookingTrackingPanel d={d} fmtDateTime={formatDateTime} titleCase={titleCase} showHeader={false} />
          )}
        </div>
        {d && target.id && canUpdate ? (
          <div className="border-t border-slate-200 bg-slate-50 px-5 py-3.5">
            <AddUpdate key={`${d.id}-${d.status}-${(d.tracking_events || []).length}`} shipment={d} onSaved={() => { load(); onChanged(); }} />
          </div>
        ) : null}
      </div>
    </div>
  );
};

// ------------------------------------------------------------------ page
const TrackingManagement = () => {
  const { hasPermission } = usePermissions();
  const canUpdate = hasPermission('edit_booking');
  const [view, setView] = useState('active');
  const [query, setQuery] = useState('');
  const [q, setQ] = useState('');
  const [carrier, setCarrier] = useState('');
  const [staleOnly, setStaleOnly] = useState(false);
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [notice, setNotice] = useState(null);
  const [, setTick] = useState(0);
  const seq = useRef(0);

  const load = useCallback(() => {
    const mine = ++seq.current;
    setLoading(true);
    api.get('/admin/tracking/board', {
      params: { view, q: q || undefined, carrier: carrier || undefined, stale: staleOnly || undefined },
    })
      .then((res) => { if (mine === seq.current) { setBoard(res.data.data); setError(null); } })
      .catch(() => { if (mine === seq.current) setError('Could not load shipments.'); })
      .finally(() => { if (mine === seq.current) setLoading(false); });
  }, [view, q, carrier, staleOnly]);

  useEffect(() => { load(); }, [load]);
  // A different board: what was ticked is no longer on screen.
  useEffect(() => { setSelected(new Set()); }, [view, q, carrier, staleOnly]);
  // Search as you type, without a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setQ(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);
  // Keep the board fresh while it is on screen (not mid-selection), and the
  // "ago" labels current.
  useEffect(() => {
    const t = setInterval(() => {
      if (document.visibilityState === 'visible' && selected.size === 0 && !open) load();
      setTick((n) => n + 1);
    }, 60000);
    return () => clearInterval(t);
  }, [load, selected.size, open]);

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = (ids, on) => setSelected((prev) => {
    const next = new Set(prev);
    ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
    return next;
  });

  const columns = board?.columns || [];
  const summary = board?.summary;
  const found = columns.reduce((n, c) => n + c.count, 0);
  const looksLikeAwb = /^[A-Za-z0-9-]{6,}$/.test(q);
  const chip = 'inline-flex items-center gap-1 px-2 py-1 text-[12px] font-semibold';

  return (
    <div className="flex h-[calc(100vh-7rem)] min-h-[560px] flex-col border border-slate-200 bg-white" data-test="tracking-board">
      {/* Summary strip */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-slate-200 bg-white px-3 py-2" data-test="board-summary">
        <h1 className="mr-1 text-[15px] font-semibold text-slate-900">Tracking</h1>
        <button
          type="button"
          onClick={() => { setStaleOnly(false); setView('exceptions'); }}
          className={`${chip} ${summary?.critical ? 'bg-red-50 text-red-700 hover:bg-red-100' : 'bg-slate-50 text-slate-500'}`}
          data-test="summary-critical"
        >
          <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
          {`${summary?.critical ?? '–'} critical exceptions`}
        </button>
        <button
          type="button"
          onClick={() => { setView('active'); setStaleOnly((v) => !v); }}
          aria-pressed={staleOnly}
          className={`${chip} ${staleOnly ? 'bg-amber-500 text-white' : summary?.stale ? 'bg-amber-50 text-amber-700 hover:bg-amber-100' : 'bg-slate-50 text-slate-500'}`}
          data-test="summary-stale"
        >
          <span className={`h-2 w-2 rounded-full ${staleOnly ? 'bg-white' : 'bg-amber-400'}`} aria-hidden="true" />
          {`${summary?.stale ?? '–'} no update for ${summary?.stale_days ?? 3}+ days`}
        </button>
        <span className="text-[12px] text-slate-500" data-test="summary-monitored">
          {`Total monitored: ${summary?.monitored ?? '–'} shipments`}
        </span>
        <span className="text-[12px] text-slate-400">{board ? `· updated ${ago(board.generated_at) === 'now' ? 'just now' : `${ago(board.generated_at)} ago`}` : ''}</span>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <select value={carrier} onChange={(e) => setCarrier(e.target.value)} className={`${field} h-8`} aria-label="Carrier" data-test="carrier-filter">
            <option value="">All carriers</option>
            {(board?.carriers || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <label className="relative block">
            <span className="sr-only">Search by name, AWB or shipment number</span>
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name, AWB or shipment no."
              className="h-8 w-56 border border-slate-300 bg-white pl-8 pr-7 text-[13px] focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-400"
              data-test="board-search"
            />
            {query ? (
              <button type="button" onClick={() => setQuery('')} className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-700" aria-label="Clear search">
                <X className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </label>
          <div className="inline-flex h-8 border border-slate-300 bg-white text-[12px] font-semibold" role="tablist">
            {[['active', 'In progress'], ['exceptions', 'Exceptions']].map(([v, text]) => (
              <button
                key={v}
                type="button"
                role="tab"
                aria-selected={view === v}
                onClick={() => { setView(v); if (v === 'exceptions') setStaleOnly(false); }}
                data-test={`view-${v}`}
                className={`px-2.5 ${view === v ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
              >
                {`${text} ${board?.totals?.[v] ?? ''}`.trim()}
              </button>
            ))}
          </div>
          <button type="button" onClick={load} className="inline-flex h-8 w-8 items-center justify-center border border-slate-300 bg-white text-slate-600 hover:bg-slate-100" aria-label="Refresh" title="Refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error ? <p className="border-b border-red-200 bg-red-50 px-3 py-1.5 text-sm text-red-700">{error}</p> : null}
      {notice ? (
        <div className="flex items-center justify-between gap-2 border-b border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[13px] text-emerald-800" data-test="board-notice">
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice(null)} className="text-emerald-700 hover:text-emerald-900" aria-label="Dismiss"><X className="h-4 w-4" /></button>
        </div>
      ) : null}
      {canUpdate && selected.size ? (
        <BulkBar
          ids={[...selected]}
          onClear={() => setSelected(new Set())}
          onDone={(text, failed) => { setNotice(text); setSelected(new Set(failed)); load(); }}
        />
      ) : null}
      {q && board && found === 0 ? (
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-3 py-2 text-[13px] text-slate-600" data-test="no-match">
          {`No shipment matches “${q}” here.`}
          {looksLikeAwb ? (
            <button type="button" onClick={() => setOpen({ awb: q.toUpperCase() })} className="font-semibold text-orange-600 hover:underline" data-test="check-awb">
              Check this AWB with the carrier
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="grid min-h-0 flex-1 auto-cols-[minmax(180px,1fr)] grid-flow-col divide-x divide-slate-200 overflow-x-auto" data-test="board-columns">
        {columns.map((col, i) => (
          <Column
            key={col.status}
            n={i + 1}
            col={col}
            onOpen={setOpen}
            selectable={canUpdate}
            selected={selected}
            onToggle={toggle}
            onToggleAll={toggleAll}
          />
        ))}
      </div>

      {open ? (
        <TrackingPopup target={open} onClose={() => setOpen(null)} onChanged={load} canUpdate={canUpdate} />
      ) : null}
    </div>
  );
};

export default TrackingManagement;
